import { useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { activationReceipt } from '../activationSummary';
import type { SetupSnapshot } from '../setupState';
import { BRAND_BUTTON } from './NarratorLine';

interface Props {
    snapshot: SetupSnapshot;
    activatedAt: string | Date;
    onClose: () => void;
    onOpenContract: () => void;
}

/**
 * The receipt — a surface, not a toast.
 *
 * A contract going live is the one green moment of the whole journey, and it is also the moment
 * the most evidence exists: which instruments are sealed, who signed each one and when, and what
 * the driver now owes and from when. A toast throws all of that away four seconds later, and
 * every admin who wanted to check it afterwards was left reading three separate screens.
 *
 * `--success` IS SPENT HERE AND NOWHERE ELSE. One green mark, once per contract; the single
 * brand-filled button on this screen is `Open contract`. Restraint is the signal.
 */
export default function ActivationReceipt({ snapshot, activatedAt, onClose, onOpenContract }: Props) {
    const receipt = activationReceipt(snapshot, activatedAt);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={receipt.title}
            className="fixed inset-0 z-[80] overflow-y-auto bg-background"
        >
            <div className="sd-receipt">
                <CheckCircle2 className="sd-rc-seal h-7 w-7" strokeWidth={1.2} />
                <h2 className="sd-rc-h">{receipt.title}</h2>
                <p className="sd-rc-sub">{receipt.subtitle}</p>

                {receipt.instruments.length > 0 && (
                    <section className="sd-rc-sec">
                        <div className="sd-rc-lab">Sealed instruments</div>
                        {receipt.instruments.map((instrument) => (
                            <div className="sd-rc-inst" key={`${instrument.docType}-${instrument.title}`}>
                                <div className="sd-rc-inst-n">
                                    {instrument.title}
                                    {instrument.when && <span className="sd-when">{instrument.when}</span>}
                                </div>
                                {instrument.evidence.length > 0 && (
                                    <ul className="sd-rc-ev">
                                        {instrument.evidence.map((line) => (
                                            <li key={line}>{line}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </section>
                )}

                {receipt.firstPayment && (
                    <section className="sd-rc-sec">
                        <div className="sd-rc-lab">First payment</div>
                        <div className="sd-rc-pay">
                            {receipt.firstPayment.amount}
                            {receipt.firstPayment.due ? ` due ${receipt.firstPayment.due}` : ''}
                        </div>
                        {receipt.firstPayment.note && (
                            <div className="sd-rc-pay-s">{receipt.firstPayment.note}</div>
                        )}
                    </section>
                )}

                {receipt.vehicleLine && (
                    <section className="sd-rc-sec">
                        <div className="sd-rc-lab">Vehicle</div>
                        <div className="sd-rc-pay-s mt-0">{receipt.vehicleLine}</div>
                    </section>
                )}

                <div className="mt-9 flex gap-2.5">
                    <Button className={cn(BRAND_BUTTON)} onClick={onOpenContract}>
                        Open contract
                    </Button>
                    <Button variant="ghost" onClick={onClose}>
                        Back to setup
                    </Button>
                </div>
            </div>
        </div>
    );
}
